import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Avatar,
  alpha,
  TextField,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  AdminPanelSettings as AdminIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  Map as MapIcon,
  TableChart as ExcelIcon,
  Help as HelpIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountCircle as AccountCircleIcon,
  ViewColumn as ViewColumnIcon,
  PlaylistAddCheck as AssessmentIcon
} from '@mui/icons-material';
import { sites, pdf, riskAnalyses, operatingBudgets, investmentBudgets, assessments } from '../services/api';
import { buildAssessmentsBookPdf } from '../utils/assessmentPdf';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';
import { getScoreColor, formatNumber } from '../utils/scoreColors';
import SiteMap from '../components/SiteMap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Colonnes proposées dans le sélecteur de tri des tuiles
const SORT_OPTIONS = [
  { value: 'nom_site', labelKey: 'siteName' },
  { value: 'ville', labelKey: 'city' },
  { value: 'pays', labelKey: 'country' },
  { value: 'province', labelKey: 'province' },
  { value: 'montant_depenses', labelKey: 'expensesAmount' },
  { value: 'montant_revenus', labelKey: 'revenuesAmount' },
  { value: 'budget_investissement', labelKey: 'investmentBudget' },
  { value: 'maxScore', labelKey: 'riskScores' },
  { value: 'riskCount', labelKey: 'riskCount' },
  { value: 'devise_pays', labelKey: 'currency' }
];
const NUMERIC_SORT_COLUMNS = ['montant_depenses', 'montant_revenus', 'budget_investissement'];

function Dashboard({ onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sitesList, setSitesList] = useState([]);
  const [language, setLanguage] = useState(i18n.language || 'fr');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [radarDialogOpen, setRadarDialogOpen] = useState(false);
  const [selectedSiteForRadar, setSelectedSiteForRadar] = useState(null);
  const [radarData, setRadarData] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [mapOpen, setMapOpen] = useState(true);
  const [excelMenuAnchor, setExcelMenuAnchor] = useState(null);
  const [exportingAssessments, setExportingAssessments] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadSites();
    checkAdminStatus();
  }, []);

  useEffect(() => {
    // Synchroniser l'état local avec la langue de i18n
    setLanguage(i18n.language);
  }, [i18n.language]);

  const checkAdminStatus = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && (user.role === 'super_admin' || user.role === 'admin_province')) {
      setIsAdmin(true);
    }
  };

  const loadSites = async () => {
    try {
      const response = await sites.getAll();
      const sitesData = response.data;

      // Charger les risques pour chaque site afin de calculer les scores
      const sitesWithScores = await Promise.all(
        sitesData.map(async (site) => {
          try {
            const risksResponse = await riskAnalyses.getBySite(site.id);
            const risks = risksResponse.data;

            if (risks.length > 0) {
              const scores = risks.map(risk => {
                let critereCount = 0;
                if (risk.urgence) critereCount++;
                if (risk.securite) critereCount++;
                if (risk.reglementaire) critereCount++;
                if (risk.financier) critereCount++;
                const ponderation = parseFloat(risk.ponderation_geo || 100) / 100;
                return parseFloat((critereCount * ponderation).toFixed(2));
              });

              const minScore = Math.min(...scores);
              const maxScore = Math.max(...scores);

              return { ...site, minScore, maxScore, riskCount: risks.length };
            }

            return { ...site, minScore: null, maxScore: null, riskCount: 0 };
          } catch (error) {
            console.error(`Error loading risks for site ${site.id}:`, error);
            return { ...site, minScore: null, maxScore: null, riskCount: 0 };
          }
        })
      );

      setSitesList(sitesWithScores);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    changeLanguage(lang);
  };

  const handleEdit = (siteId) => {
    navigate(`/site/${siteId}`);
  };

  const handleDelete = (site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await sites.delete(siteToDelete.id);
      loadSites();
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  const handleGeneratePdf = async (siteId, siteName) => {
    try {
      const currentLang = i18n.language || 'fr';
      const response = await pdf.generateSitePdf(siteId, currentLang);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `site-${siteName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleMapSiteClick = (siteId) => {
    navigate(`/site/${siteId}`);
  };

  const handleDownloadUserGuide = () => {
    // Mapper la langue actuelle vers le fichier de guide correspondant
    const guideFiles = {
      fr: 'guide-utilisateur-fr.html',
      en: 'user-guide-en.html',
      pt: 'guia-usuario-pt.html',
      mg: 'torolalana-mg.html'
    };

    const currentLang = i18n.language || 'fr';
    const guideFile = guideFiles[currentLang] || guideFiles['fr'];

    // Ouvrir le guide dans un nouvel onglet
    window.open(`/guides/${guideFile}`, '_blank');
  };

  const THEMATIQUES = [
    { value: 'statut_juridique', label: 'Statut juridique du bien' },
    { value: 'assurance', label: 'Assurance du patrimoine' },
    { value: 'conformite', label: 'Conformité réglementaire' },
    { value: 'gros_oeuvre', label: 'État du gros œuvre' },
    { value: 'reseaux', label: 'Réseaux techniques' },
    { value: 'patrimoine_protege', label: 'Patrimoine protégé / classé' },
    { value: 'environnement', label: 'Contraintes environnementales / climatiques' },
    { value: 'incidents', label: 'Incidents récents / dégradations' },
    { value: 'audits', label: 'Audits et contrôles' },
    { value: 'pmr', label: 'Accessibilité PMR' },
    { value: 'incendie', label: 'Sécurité incendie' },
    { value: 'autre', label: 'Autre' }
  ];

  const handleOpenRadarChart = async (site) => {
    try {
      const response = await riskAnalyses.getBySite(site.id);
      const risks = response.data;

      // Calculer la moyenne des scores par thématique
      const thematiquesMap = {};

      THEMATIQUES.forEach(thematique => {
        thematiquesMap[thematique.value] = { total: 0, count: 0, label: thematique.label };
      });

      risks.forEach(risk => {
        const thematique = risk.thematique;
        if (thematique && thematiquesMap[thematique]) {
          // Calculer le score du risque
          let critereCount = 0;
          if (risk.urgence) critereCount++;
          if (risk.securite) critereCount++;
          if (risk.reglementaire) critereCount++;
          if (risk.financier) critereCount++;
          const ponderation = parseFloat(risk.ponderation_geo || 100) / 100;
          const score = critereCount * ponderation;

          thematiquesMap[thematique].total += score;
          thematiquesMap[thematique].count++;
        }
      });

      // Créer les données du radar
      const chartData = THEMATIQUES.map(thematique => {
        const data = thematiquesMap[thematique.value];
        const avgScore = data.count > 0 ? data.total / data.count : 0;
        return {
          thematique: thematique.label,
          score: parseFloat(avgScore.toFixed(2))
        };
      });

      setRadarData(chartData);
      setSelectedSiteForRadar(site);
      setRadarDialogOpen(true);
    } catch (error) {
      console.error('Error loading radar data:', error);
    }
  };

  const handleDownloadRadarChart = () => {
    const svg = document.querySelector('#radar-chart-container svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    // Taille augmentée pour inclure titre, légende et tableau
    canvas.width = 1000;
    canvas.height = 1400;

    img.onload = () => {
      // Fond blanc
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Titre avec nom du site
      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Analyse des risques', canvas.width / 2, 50);

      // Nom du site et ville
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 24px Arial';
      ctx.fillText(selectedSiteForRadar.nom_site, canvas.width / 2, 85);

      if (selectedSiteForRadar.ville || selectedSiteForRadar.pays) {
        ctx.fillStyle = '#666666';
        ctx.font = '18px Arial';
        const location = [selectedSiteForRadar.ville, selectedSiteForRadar.pays].filter(Boolean).join(', ');
        ctx.fillText(location, canvas.width / 2, 115);
      }

      // Graphique radar
      ctx.drawImage(img, 100, 140, 800, 600);

      // Légende des couleurs
      const legendY = 760;
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('Échelle de criticité :', 50, legendY);

      const legendItems = [
        { color: '#2e7d32', label: 'Score ≤ 1 (Faible)', x: 50 },
        { color: '#fbc02d', label: '1 < Score ≤ 2 (Modéré)', x: 280 },
        { color: '#ed6c02', label: '2 < Score ≤ 3 (Élevé)', x: 550 },
        { color: '#d32f2f', label: 'Score > 3 (Critique)', x: 770 }
      ];

      legendItems.forEach(item => {
        ctx.fillStyle = item.color;
        ctx.fillRect(item.x, legendY + 10, 20, 20);
        ctx.fillStyle = '#000000';
        ctx.font = '14px Arial';
        ctx.fillText(item.label, item.x + 30, legendY + 25);
      });

      // Tableau des scores
      const tableY = 830;
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 18px Arial';
      ctx.fillText('Scores par thématique :', 50, tableY);

      // En-tête du tableau
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(50, tableY + 15, 900, 35);
      ctx.strokeStyle = '#cccccc';
      ctx.strokeRect(50, tableY + 15, 900, 35);

      ctx.fillStyle = '#000000';
      ctx.font = 'bold 14px Arial';
      ctx.fillText('Thématique', 70, tableY + 38);
      ctx.fillText('Score moyen', 820, tableY + 38);

      // Lignes du tableau
      const sortedRadarData = radarData
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score);

      sortedRadarData.forEach((item, index) => {
        const rowY = tableY + 50 + (index * 40);

        // Ligne
        ctx.fillStyle = index % 2 === 0 ? '#ffffff' : '#f9f9f9';
        ctx.fillRect(50, rowY, 900, 40);
        ctx.strokeStyle = '#e0e0e0';
        ctx.strokeRect(50, rowY, 900, 40);

        // Thématique
        ctx.fillStyle = '#000000';
        ctx.font = '13px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(item.thematique, 70, rowY + 25);

        // Score avec couleur
        const scoreColor = getScoreColor(item.score);
        ctx.fillStyle = scoreColor;
        ctx.fillRect(810, rowY + 8, 80, 24);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(item.score.toFixed(2), 850, rowY + 25);
      });

      // Télécharger l'image
      canvas.toBlob((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `radar-${selectedSiteForRadar.nom_site}.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const calculateTotals = (sitesArray) => {
    const totalDepenses = sitesArray.reduce((sum, site) => sum + parseFloat(site.montant_depenses || 0), 0);
    const totalRevenus = sitesArray.reduce((sum, site) => sum + parseFloat(site.montant_revenus || 0), 0);
    const totalInvestissement = sitesArray.reduce((sum, site) => sum + parseFloat(site.budget_investissement || 0), 0);
    return { totalDepenses, totalRevenus, totalInvestissement };
  };

  const handleSortColumnChange = (e) => {
    setSortColumn(e.target.value || null);
    setSortDirection('asc');
  };

  const getFilteredSites = () => {
    if (!searchQuery) return sitesList;

    const query = searchQuery.toLowerCase();
    return sitesList.filter(site => {
      return (
        (site.nom_site && site.nom_site.toLowerCase().includes(query)) ||
        (site.ville && site.ville.toLowerCase().includes(query)) ||
        (site.pays && site.pays.toLowerCase().includes(query)) ||
        (site.province && site.province.toLowerCase().includes(query)) ||
        (site.montant_depenses && site.montant_depenses.toString().includes(query)) ||
        (site.montant_revenus && site.montant_revenus.toString().includes(query)) ||
        (site.budget_investissement && site.budget_investissement.toString().includes(query)) ||
        (site.devise_pays && site.devise_pays.toLowerCase().includes(query))
      );
    });
  };

  const getSortedSites = () => {
    const filteredSites = getFilteredSites();
    if (!sortColumn) return filteredSites;

    return [...filteredSites].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Gestion spéciale pour les scores risques
      if (sortColumn === 'minScore' || sortColumn === 'maxScore') {
        aValue = aValue !== null ? aValue : -Infinity;
        bValue = bValue !== null ? bValue : -Infinity;
      }

      // Conversion en nombre si nécessaire
      if (NUMERIC_SORT_COLUMNS.includes(sortColumn)) {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      // Comparaison
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedSites = getSortedSites();

  // Calculer les totaux basés sur les sites filtrés
  const { totalDepenses, totalRevenus, totalInvestissement } = calculateTotals(sortedSites);
  const isFiltered = searchQuery.length > 0;

  const getProvinceLabel = (province) => {
    if (!province) return '-';
    return t(`provinces.${province}`);
  };

  const exportToPdf = () => {
    const doc = new jsPDF('landscape');
    const sitesToExport = sortedSites;
    const dateStr = new Date().toLocaleDateString();
    const pageWidth = doc.internal.pageSize.getWidth();

    // === EN-TÊTE DESIGN (couleurs pastel sobres) ===

    // Bande supérieure avec couleur pastel douce
    doc.setFillColor(245, 247, 250); // Gris très clair
    doc.rect(0, 0, pageWidth, 50, 'F');

    // Ligne décorative en haut
    doc.setFillColor(120, 144, 175); // Bleu-gris doux
    doc.rect(0, 0, pageWidth, 3, 'F');

    // Logo/Titre principal
    doc.setTextColor(70, 85, 105); // Gris bleuté foncé
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('PATRIMOINE', 14, 25);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 135, 155); // Gris moyen
    doc.text(t('sitesList'), 14, 38);

    // Date et filtre en haut à droite
    doc.setFontSize(10);
    doc.setTextColor(100, 115, 135);
    doc.text(dateStr, pageWidth - 14, 20, { align: 'right' });
    if (searchQuery) {
      doc.setFontSize(8);
      doc.text(`${t('searchFilter')}: "${searchQuery}"`, pageWidth - 14, 30, { align: 'right' });
    }

    // === BLOC STATISTIQUES (couleurs pastel) ===
    const statsY = 60;
    const cardWidth = 64;
    const cardHeight = 35;
    const cardSpacing = 8;
    const startX = (pageWidth - (4 * cardWidth + 3 * cardSpacing)) / 2;

    // Carte 1 - Sites (bleu pastel)
    doc.setFillColor(214, 228, 240); // Bleu pastel clair
    doc.roundedRect(startX, statsY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(170, 195, 220); // Bordure bleu pastel
    doc.setLineWidth(0.5);
    doc.roundedRect(startX, statsY, cardWidth, cardHeight, 3, 3, 'S');
    doc.setTextColor(70, 100, 130); // Texte bleu foncé
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(isFiltered ? t('filteredSites') : t('totalSites'), startX + cardWidth / 2, statsY + 12, { align: 'center' });
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 80, 110);
    doc.text(`${sitesToExport.length}`, startX + cardWidth / 2, statsY + 27, { align: 'center' });

    // Carte 2 - Montant Dépenses (rose pastel)
    const card2X = startX + cardWidth + cardSpacing;
    doc.setFillColor(245, 222, 230); // Rose pastel clair
    doc.roundedRect(card2X, statsY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(220, 190, 200); // Bordure rose pastel
    doc.roundedRect(card2X, statsY, cardWidth, cardHeight, 3, 3, 'S');
    doc.setTextColor(140, 90, 110); // Texte rose foncé
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(t('expensesAmount'), card2X + cardWidth / 2, statsY + 12, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 70, 90);
    doc.text(formatNumber(totalDepenses), card2X + cardWidth / 2, statsY + 27, { align: 'center' });

    // Carte 3 - Montant Revenus (ambre pastel)
    const cardRevX = card2X + cardWidth + cardSpacing;
    doc.setFillColor(250, 236, 210); // Ambre pastel clair
    doc.roundedRect(cardRevX, statsY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(225, 205, 165); // Bordure ambre pastel
    doc.roundedRect(cardRevX, statsY, cardWidth, cardHeight, 3, 3, 'S');
    doc.setTextColor(140, 105, 55); // Texte ambre foncé
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(t('revenuesAmount'), cardRevX + cardWidth / 2, statsY + 12, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 85, 35);
    doc.text(formatNumber(totalRevenus), cardRevX + cardWidth / 2, statsY + 27, { align: 'center' });

    // Carte 4 - Budget Investissement (vert pastel)
    const card3X = cardRevX + cardWidth + cardSpacing;
    doc.setFillColor(215, 235, 225); // Vert pastel clair
    doc.roundedRect(card3X, statsY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(180, 210, 195); // Bordure vert pastel
    doc.roundedRect(card3X, statsY, cardWidth, cardHeight, 3, 3, 'S');
    doc.setTextColor(70, 115, 95); // Texte vert foncé
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(t('investmentBudget'), card3X + cardWidth / 2, statsY + 12, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 95, 75);
    doc.text(formatNumber(totalInvestissement), card3X + cardWidth / 2, statsY + 27, { align: 'center' });

    // Ligne séparatrice
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(14, statsY + cardHeight + 10, pageWidth - 14, statsY + cardHeight + 10);

    // === TABLEAU DES SITES ===

    // Données du tableau
    const tableData = sitesToExport.map(site => [
      site.nom_site || '-',
      site.ville || '-',
      site.pays || '-',
      getProvinceLabel(site.province),
      formatNumber(site.montant_depenses || 0),
      formatNumber(site.montant_revenus || 0),
      site.minScore !== null && site.maxScore !== null
        ? `${site.minScore.toFixed(2)} - ${site.maxScore.toFixed(2)}`
        : '-',
      site.riskCount || 0,
      formatNumber(site.budget_investissement || 0),
      site.devise_pays ? t(`currencies.${site.devise_pays}`) : '-'
    ]);

    // En-têtes du tableau
    const headers = [
      [
        t('siteName'),
        t('city'),
        t('country'),
        t('province'),
        t('expensesAmount'),
        t('revenuesAmount'),
        t('riskScores'),
        t('riskCount'),
        t('investmentBudget'),
        t('currency')
      ]
    ];

    // Générer le tableau
    autoTable(doc, {
      head: headers,
      body: tableData,
      startY: statsY + cardHeight + 18,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: [60, 70, 80], // Texte gris foncé
      },
      headStyles: {
        fillColor: [120, 144, 175], // Bleu-gris doux (même que la ligne d'en-tête)
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252], // Gris très léger
      },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'center' },
        7: { halign: 'center' },
        8: { halign: 'right' },
        9: { halign: 'center' },
      },
    });

    // === PIED DE PAGE ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} / ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
      doc.text(
        `Généré le ${dateStr}`,
        pageWidth - 14,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'right' }
      );
    }

    // Télécharger le PDF
    doc.save(`liste-sites-${dateStr}.pdf`);
  };

  // Recueil PDF des derniers diagnostics terminés des sites affichés (filtre de recherche et tri appliqués)
  const exportAssessmentsPdf = async () => {
    setExcelMenuAnchor(null);
    if (sortedSites.length === 0) return;
    setExportingAssessments(true);
    try {
      const res = await assessments.getLatestForExport(sortedSites.map((s) => s.id));
      if (res.data.length === 0) {
        setToast(t('noAssessmentsToExport'));
        return;
      }
      const { doc, filename } = buildAssessmentsBookPdf(res.data, {
        t, lang: i18n.language, sites: sortedSites, searchQuery
      });
      doc.save(filename);
    } catch (error) {
      console.error('Error exporting assessments:', error);
      setToast(t('exportAssessmentsError'));
    } finally {
      setExportingAssessments(false);
    }
  };

  const exportToExcel = () => {
    const sitesToExport = sortedSites;
    const dateStr = new Date().toLocaleDateString();

    // Préparer les données
    const excelData = sitesToExport.map(site => ({
      [t('siteName')]: site.nom_site || '',
      [t('city')]: site.ville || '',
      [t('country')]: site.pays || '',
      [t('province')]: getProvinceLabel(site.province),
      [t('expensesAmount')]: site.montant_depenses || 0,
      [t('revenuesAmount')]: site.montant_revenus || 0,
      [t('riskScores')]: site.minScore !== null && site.maxScore !== null
        ? `${site.minScore.toFixed(2)} - ${site.maxScore.toFixed(2)}`
        : '',
      [t('riskCount')]: site.riskCount || 0,
      [t('investmentBudget')]: site.budget_investissement || 0,
      [t('currency')]: site.devise_pays ? t(`currencies.${site.devise_pays}`) : ''
    }));

    // Créer le workbook et la feuille
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sites');

    // Ajuster la largeur des colonnes
    const colWidths = [
      { wch: 30 }, // Nom site
      { wch: 20 }, // Ville
      { wch: 15 }, // Pays
      { wch: 20 }, // Province
      { wch: 18 }, // Montant dépenses
      { wch: 18 }, // Montant revenus
      { wch: 15 }, // Scores risques
      { wch: 12 }, // Nombre de risques
      { wch: 18 }, // Budget investissement
      { wch: 12 }, // Devise
    ];
    ws['!cols'] = colWidths;

    // Télécharger le fichier Excel
    XLSX.writeFile(wb, `liste-sites-${dateStr}.xlsx`);
  };

  const exportSiteOperatingBudget = async (site) => {
    try {
      const response = await operatingBudgets.getBySite(site.id);
      const budgets = response.data;

      if (!budgets || budgets.length === 0) return;

      const expenseRows = budgets.filter(b => (b.categorie_principale || '') !== 'REVENUS');
      const revenueRows = budgets.filter(b => (b.categorie_principale || '') === 'REVENUS');
      const totalExpenses = expenseRows.reduce((sum, b) => sum + parseFloat(b.montant || 0), 0);
      const totalRevenues = revenueRows.reduce((sum, b) => sum + parseFloat(b.montant || 0), 0);

      const getItemLabel = (item) => {
        const key = `budgetItems.${(item || '').toLowerCase().replace(/ /g, '_')}`;
        const translated = t(key);
        return translated === key ? item : translated;
      };
      const getCategoryLabel = (cat) => {
        const key = `budgetCategories.${(cat || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? cat : translated;
      };

      const currencyLabel = site.devise_pays ? t(`currencies.${site.devise_pays}`) : '';
      const provinceLabel = getProvinceLabel(site.province);
      const siteInfo = [site.nom_site, site.adresse, provinceLabel].filter(Boolean).join(' - ');

      const buildingLabel = (b) => b.batiment_numero
        ? `${t('building')} ${b.batiment_numero}`
        : t('entireSite');

      const rows = [];
      const EMPTY = ['', '', '', '', '', ''];

      // Ligne 1 : informations du site
      rows.push([siteInfo, '', '', '', '', '']);
      rows.push(EMPTY);
      // En-têtes de colonnes
      rows.push([t('category'), t('budgetItem'), t('building'), t('annualAmount'), t('currency'), t('comment')]);

      // Dépenses
      expenseRows.forEach(b => {
        rows.push([
          getCategoryLabel(b.categorie_principale),
          getItemLabel(b.item_budget),
          buildingLabel(b),
          parseFloat(b.montant || 0),
          currencyLabel,
          b.commentaire || ''
        ]);
      });

      // Ligne sous-total dépenses
      rows.push(['', t('totalExpenses'), '', totalExpenses, currencyLabel, '']);

      // Ligne vide de séparation
      rows.push(EMPTY);

      // Revenus
      revenueRows.forEach(b => {
        rows.push([
          getCategoryLabel(b.categorie_principale),
          getItemLabel(b.item_budget),
          buildingLabel(b),
          parseFloat(b.montant || 0),
          currencyLabel,
          b.commentaire || ''
        ]);
      });

      // Ligne sous-total revenus
      rows.push(['', t('totalRevenues'), '', totalRevenues, currencyLabel, '']);

      // Ligne vide + total global
      rows.push(EMPTY);
      rows.push(['', t('operatingBudgetTotal'), '', totalExpenses - totalRevenues, currencyLabel, '']);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 25 }, { wch: 50 }, { wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 40 }];
      // Fusionner la première ligne sur toutes les colonnes
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('operatingBudget'));
      XLSX.writeFile(wb, `budget-exploitation-${site.nom_site}.xlsx`);
    } catch (error) {
      console.error('Error exporting operating budget:', error);
    }
  };

  const exportSiteInvestmentBudget = async (site) => {
    try {
      const response = await investmentBudgets.getBySite(site.id);
      const budgets = response.data;

      if (!budgets || budgets.length === 0) return;

      const getThematiqueLabel = (thematique) => {
        const key = `thematiques.${(thematique || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? thematique : translated;
      };

      const getRiskItemLabel = (itemJson, itemAutre) => {
        let items = [];
        try {
          const parsed = JSON.parse(itemJson);
          items = Array.isArray(parsed) ? parsed : [itemJson];
        } catch {
          items = itemJson ? [itemJson] : [];
        }
        return items.map(item => {
          if (item === 'autre' && itemAutre) return itemAutre;
          const key = `riskItems.${item}`;
          const translated = t(key);
          return translated === key ? item : translated;
        }).join(', ');
      };

      const currencyLabel = site.devise_pays ? t(`currencies.${site.devise_pays}`) : '';
      const provinceLabel = getProvinceLabel(site.province);
      const siteInfo = [site.nom_site, site.adresse, provinceLabel].filter(Boolean).join(' - ');

      const currentYear = new Date().getFullYear();
      // Année de référence commune (du premier budget) pour libellés colonnes
      const baseYear = parseInt(budgets[0]?.annee_reference, 10) || currentYear;
      const yearLabel = (offset) => `${baseYear + offset}`;

      const rows = [];
      const EMPTY = Array(19).fill('');

      // Ligne 1 : informations du site
      rows.push([siteInfo, ...Array(18).fill('')]);
      rows.push(EMPTY);
      // En-têtes de colonnes
      rows.push([
        t('theme'), t('item'), t('building'), t('referenceYear'), t('score'),
        t('investmentAmount'), t('adjustedAmount'),
        `${t('forecast')} ${yearLabel(0)}`, `${t('forecast')} ${yearLabel(1)}`, `${t('forecast')} ${yearLabel(2)}`,
        `${t('forecast')} ${yearLabel(3)}`, `${t('forecast')} ${yearLabel(4)}`, `${t('forecast')} ${yearLabel(5)}`,
        `${t('realized')} ${yearLabel(0)}`, `${t('realized')} ${yearLabel(1)}`, `${t('realized')} ${yearLabel(2)}`,
        `${t('realized')} ${yearLabel(3)}`, `${t('realized')} ${yearLabel(4)}`, `${t('realized')} ${yearLabel(5)}`,
        t('totalRealized'),
        t('status'), t('currency')
      ]);

      let totalMontant = 0;
      let totalRealise = 0;

      budgets.forEach(b => {
        const montantPondere = parseFloat(b.montant_investissement || 0) * (1 + parseFloat(b.ajustement_pourcentage || 0) / 100);
        const validationLabel = b.validation_status ? t(`validationStatus.${b.validation_status}`) : t('validationStatus.en_attente');
        const scopeLabel = b.batiment_numero
          ? `${t('building')} ${b.batiment_numero}`
          : t('entireSite');
        const refYear = parseInt(b.annee_reference, 10) || currentYear;

        const realiseTotal = ['montant_realise_annee_n', 'montant_realise_annee_n1', 'montant_realise_annee_n2',
          'montant_realise_annee_n3', 'montant_realise_annee_n4', 'montant_realise_annee_n5']
          .reduce((s, k) => s + parseFloat(b[k] || 0), 0);

        rows.push([
          getThematiqueLabel(b.thematique),
          getRiskItemLabel(b.item, b.item_autre),
          scopeLabel,
          refYear,
          b.score != null ? parseFloat(b.score).toFixed(2) : '',
          parseFloat(b.montant_investissement || 0),
          Math.round(montantPondere),
          parseFloat(b.montant_annee_n || 0),
          parseFloat(b.montant_annee_n1 || 0),
          parseFloat(b.montant_annee_n2 || 0),
          parseFloat(b.montant_annee_n3 || 0),
          parseFloat(b.montant_annee_n4 || 0),
          parseFloat(b.montant_annee_n5 || 0),
          parseFloat(b.montant_realise_annee_n || 0),
          parseFloat(b.montant_realise_annee_n1 || 0),
          parseFloat(b.montant_realise_annee_n2 || 0),
          parseFloat(b.montant_realise_annee_n3 || 0),
          parseFloat(b.montant_realise_annee_n4 || 0),
          parseFloat(b.montant_realise_annee_n5 || 0),
          realiseTotal,
          validationLabel,
          currencyLabel
        ]);

        totalMontant += montantPondere;
        totalRealise += realiseTotal;
      });

      // Ligne total
      rows.push(EMPTY);
      const totalRow = Array(22).fill('');
      totalRow[5] = t('investmentBudget') + ' - Total';
      totalRow[6] = Math.round(totalMontant);
      totalRow[19] = Math.round(totalRealise);
      totalRow[21] = currencyLabel;
      rows.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: 25 }, { wch: 30 }, { wch: 18 }, { wch: 11 }, { wch: 8 },
        { wch: 16 }, { wch: 16 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 16 }, { wch: 15 }, { wch: 10 }
      ];
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 21 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('investmentBudget'));
      XLSX.writeFile(wb, `budget-investissement-${site.nom_site}.xlsx`);
    } catch (error) {
      console.error('Error exporting investment budget:', error);
    }
  };

  const exportConsolidatedOperatingBudget = async () => {
    setExcelMenuAnchor(null);
    try {
      const dateStr = new Date().toLocaleDateString();

      const getItemLabel = (item) => {
        const key = `budgetItems.${(item || '').toLowerCase().replace(/ /g, '_')}`;
        const translated = t(key);
        return translated === key ? item : translated;
      };
      const getCategoryLabel = (cat) => {
        const key = `budgetCategories.${(cat || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? cat : translated;
      };

      // Grouper les sites par province
      const sitesByProvince = {};
      for (const site of sortedSites) {
        const province = site.province || 'N/A';
        if (!sitesByProvince[province]) sitesByProvince[province] = [];
        sitesByProvince[province].push(site);
      }

      // Récupérer les budgets pour chaque site et consolider par province et devise
      const consolidatedData = {};
      for (const [province, provinceSites] of Object.entries(sitesByProvince)) {
        if (!consolidatedData[province]) consolidatedData[province] = {};
        for (const site of provinceSites) {
          const currency = site.devise_pays || 'N/A';
          if (!consolidatedData[province][currency]) {
            consolidatedData[province][currency] = { items: {} };
          }
          try {
            const response = await operatingBudgets.getBySite(site.id);
            const budgets = response.data || [];
            for (const b of budgets) {
              const key = `${b.categorie_principale}||${b.item_budget}`;
              if (!consolidatedData[province][currency].items[key]) {
                consolidatedData[province][currency].items[key] = {
                  categorie_principale: b.categorie_principale,
                  item_budget: b.item_budget,
                  montant: 0
                };
              }
              consolidatedData[province][currency].items[key].montant += parseFloat(b.montant || 0);
            }
          } catch (err) {
            console.error(`Error fetching operating budget for site ${site.id}:`, err);
          }
        }
      }

      // Générer le fichier Excel
      const rows = [];
      rows.push([t('exportConsolidatedOperating'), '', '', '', '']);
      rows.push(['', '', '', '', '']);
      rows.push([t('province'), t('category'), t('budgetItem'), t('annualAmount'), t('currency')]);

      const grandTotals = {};

      for (const [province, currencyData] of Object.entries(consolidatedData)) {
        const provinceLabel = getProvinceLabel(province);

        for (const [currency, data] of Object.entries(currencyData)) {
          const currencyLabel = currency !== 'N/A' ? t(`currencies.${currency}`) : '';
          const items = Object.values(data.items);

          const expenses = items.filter(i => (i.categorie_principale || '') !== 'REVENUS');
          const revenues = items.filter(i => (i.categorie_principale || '') === 'REVENUS');
          const totalExpenses = expenses.reduce((sum, i) => sum + i.montant, 0);
          const totalRevenues = revenues.reduce((sum, i) => sum + i.montant, 0);

          for (const item of expenses) {
            rows.push([
              provinceLabel,
              getCategoryLabel(item.categorie_principale),
              getItemLabel(item.item_budget),
              item.montant,
              currencyLabel
            ]);
          }

          if (expenses.length > 0) {
            rows.push(['', '', t('subtotal') + ' ' + t('totalExpenses'), totalExpenses, currencyLabel]);
          }

          for (const item of revenues) {
            rows.push([
              provinceLabel,
              getCategoryLabel(item.categorie_principale),
              getItemLabel(item.item_budget),
              item.montant,
              currencyLabel
            ]);
          }

          if (revenues.length > 0) {
            rows.push(['', '', t('subtotal') + ' ' + t('totalRevenues'), totalRevenues, currencyLabel]);
          }

          rows.push(['', '', t('subtotal') + ' ' + provinceLabel + ' (' + currencyLabel + ')', totalExpenses - totalRevenues, currencyLabel]);
          rows.push(['', '', '', '', '']);

          if (!grandTotals[currency]) grandTotals[currency] = { expenses: 0, revenues: 0 };
          grandTotals[currency].expenses += totalExpenses;
          grandTotals[currency].revenues += totalRevenues;
        }
      }

      for (const [currency, totals] of Object.entries(grandTotals)) {
        const currencyLabel = currency !== 'N/A' ? t(`currencies.${currency}`) : '';
        rows.push(['', '', t('operatingBudgetTotal') + ' (' + currencyLabel + ')', totals.expenses - totals.revenues, currencyLabel]);
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 25 }, { wch: 30 }, { wch: 40 }, { wch: 20 }, { wch: 18 }];
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('operatingBudget'));
      XLSX.writeFile(wb, `budget-exploitation-consolide-${dateStr}.xlsx`);
    } catch (error) {
      console.error('Error exporting consolidated operating budget:', error);
    }
  };

  const exportConsolidatedInvestmentBudget = async () => {
    setExcelMenuAnchor(null);
    try {
      const dateStr = new Date().toLocaleDateString();

      const getThematiqueLabel = (thematique) => {
        const key = `thematiques.${(thematique || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? thematique : translated;
      };

      const getRiskItemLabel = (itemJson, itemAutre) => {
        let items = [];
        try {
          const parsed = JSON.parse(itemJson);
          items = Array.isArray(parsed) ? parsed : [itemJson];
        } catch {
          items = itemJson ? [itemJson] : [];
        }
        return items.map(item => {
          if (item === 'autre' && itemAutre) return itemAutre;
          const key = `riskItems.${item}`;
          const translated = t(key);
          return translated === key ? item : translated;
        }).join(', ');
      };

      // Grouper les sites par province
      const sitesByProvince = {};
      for (const site of sortedSites) {
        const province = site.province || 'N/A';
        if (!sitesByProvince[province]) sitesByProvince[province] = [];
        sitesByProvince[province].push(site);
      }

      // Récupérer les budgets d'investissement pour chaque site et grouper par province et devise
      const currentYear = new Date().getFullYear();
      const rows = [];
      const COL_COUNT = 23;
      const EMPTY = Array(COL_COUNT).fill('');
      rows.push([t('exportConsolidatedInvestment'), ...Array(COL_COUNT - 1).fill('')]);
      rows.push(EMPTY);
      rows.push([
        t('province'), t('siteName'), t('building'), t('referenceYear'), t('theme'), t('item'),
        t('score'), t('investmentAmount'), t('adjustedAmount'),
        `${t('forecast')} ref+0`, `${t('forecast')} ref+1`, `${t('forecast')} ref+2`,
        `${t('forecast')} ref+3`, `${t('forecast')} ref+4`, `${t('forecast')} ref+5`,
        `${t('realized')} ref+0`, `${t('realized')} ref+1`, `${t('realized')} ref+2`,
        `${t('realized')} ref+3`, `${t('realized')} ref+4`, `${t('realized')} ref+5`,
        t('totalRealized'),
        t('status'), t('currency')
      ].slice(0, COL_COUNT));

      const grandTotals = {};

      for (const [province, provinceSites] of Object.entries(sitesByProvince)) {
        const provinceLabel = getProvinceLabel(province);

        const sitesByCurrency = {};
        for (const site of provinceSites) {
          const currency = site.devise_pays || 'N/A';
          if (!sitesByCurrency[currency]) sitesByCurrency[currency] = [];
          sitesByCurrency[currency].push(site);
        }

        for (const [currency, currencySites] of Object.entries(sitesByCurrency)) {
          const currencyLabel = currency !== 'N/A' ? t(`currencies.${currency}`) : '';
          let currencyTotal = 0;
          let currencyRealized = 0;

          for (const site of currencySites) {
            try {
              const response = await investmentBudgets.getBySite(site.id);
              const budgets = response.data || [];

              for (const b of budgets) {
                const montantPondere = parseFloat(b.montant_investissement || 0) * (1 + parseFloat(b.ajustement_pourcentage || 0) / 100);
                const validationLabel = b.validation_status ? t(`validationStatus.${b.validation_status}`) : t('validationStatus.en_attente');
                const scopeLabel = b.batiment_numero
                  ? `${t('building')} ${b.batiment_numero}`
                  : t('entireSite');
                const refYear = parseInt(b.annee_reference, 10) || currentYear;
                const realiseTotal = ['montant_realise_annee_n', 'montant_realise_annee_n1', 'montant_realise_annee_n2',
                  'montant_realise_annee_n3', 'montant_realise_annee_n4', 'montant_realise_annee_n5']
                  .reduce((s, k) => s + parseFloat(b[k] || 0), 0);

                rows.push([
                  provinceLabel,
                  site.nom_site || '',
                  scopeLabel,
                  refYear,
                  getThematiqueLabel(b.thematique),
                  getRiskItemLabel(b.item, b.item_autre),
                  b.score != null ? parseFloat(b.score).toFixed(2) : '',
                  parseFloat(b.montant_investissement || 0),
                  Math.round(montantPondere),
                  parseFloat(b.montant_annee_n || 0),
                  parseFloat(b.montant_annee_n1 || 0),
                  parseFloat(b.montant_annee_n2 || 0),
                  parseFloat(b.montant_annee_n3 || 0),
                  parseFloat(b.montant_annee_n4 || 0),
                  parseFloat(b.montant_annee_n5 || 0),
                  parseFloat(b.montant_realise_annee_n || 0),
                  parseFloat(b.montant_realise_annee_n1 || 0),
                  parseFloat(b.montant_realise_annee_n2 || 0),
                  parseFloat(b.montant_realise_annee_n3 || 0),
                  parseFloat(b.montant_realise_annee_n4 || 0),
                  parseFloat(b.montant_realise_annee_n5 || 0),
                  realiseTotal,
                  validationLabel,
                  currencyLabel
                ]);

                currencyTotal += montantPondere;
                currencyRealized += realiseTotal;
              }
            } catch (err) {
              console.error(`Error fetching investment budget for site ${site.id}:`, err);
            }
          }

          const subtotalRow = Array(COL_COUNT).fill('');
          subtotalRow[5] = `${t('subtotal')} ${provinceLabel} (${currencyLabel})`;
          subtotalRow[8] = Math.round(currencyTotal);
          subtotalRow[21] = Math.round(currencyRealized);
          subtotalRow[23] = currencyLabel;
          rows.push(subtotalRow);
          rows.push(EMPTY);

          if (!grandTotals[currency]) grandTotals[currency] = { forecast: 0, realized: 0 };
          grandTotals[currency].forecast += currencyTotal;
          grandTotals[currency].realized += currencyRealized;
        }
      }

      for (const [currency, totals] of Object.entries(grandTotals)) {
        const currencyLabel = currency !== 'N/A' ? t(`currencies.${currency}`) : '';
        const totalRow = Array(COL_COUNT).fill('');
        totalRow[5] = `${t('investmentBudget')} - Total (${currencyLabel})`;
        totalRow[8] = Math.round(totals.forecast);
        totalRow[21] = Math.round(totals.realized);
        totalRow[23] = currencyLabel;
        rows.push(totalRow);
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 11 }, { wch: 22 }, { wch: 30 },
        { wch: 8 }, { wch: 16 }, { wch: 16 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 16 }, { wch: 15 }, { wch: 10 }
      ];
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COL_COUNT - 1 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('investmentBudget'));
      XLSX.writeFile(wb, `budget-investissement-consolide-${dateStr}.xlsx`);
    } catch (error) {
      console.error('Error exporting consolidated investment budget:', error);
    }
  };

  const exportDetailedOperatingBudget = async () => {
    setExcelMenuAnchor(null);
    try {
      const dateStr = new Date().toLocaleDateString();

      const getItemLabel = (item) => {
        const key = `budgetItems.${(item || '').toLowerCase().replace(/ /g, '_')}`;
        const translated = t(key);
        return translated === key ? item : translated;
      };
      const getCategoryLabel = (cat) => {
        const key = `budgetCategories.${(cat || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? cat : translated;
      };

      // Grouper les sites par province puis par devise
      const groups = {};
      for (const site of sortedSites) {
        const province = site.province || 'N/A';
        const currency = site.devise_pays || 'N/A';
        const key = `${province}||${currency}`;
        if (!groups[key]) groups[key] = { province, currency, sites: [] };
        groups[key].sites.push(site);
      }

      // Récupérer tous les budgets d'exploitation
      const allBudgets = {};
      for (const site of sortedSites) {
        try {
          const response = await operatingBudgets.getBySite(site.id);
          allBudgets[site.id] = response.data || [];
        } catch (err) {
          console.error(`Error fetching operating budget for site ${site.id}:`, err);
          allBudgets[site.id] = [];
        }
      }

      const rows = [];
      rows.push([t('exportDetailedOperating')]);
      rows.push([]);

      for (const group of Object.values(groups)) {
        const provinceLabel = getProvinceLabel(group.province);
        const currencyLabel = group.currency !== 'N/A' ? t(`currencies.${group.currency}`) : '';
        const groupSites = group.sites;

        // Collecter tous les items de budget uniques dans l'ordre
        const itemOrder = [];
        const itemSet = new Set();
        for (const site of groupSites) {
          for (const b of (allBudgets[site.id] || [])) {
            const k = `${b.categorie_principale}||${b.item_budget}`;
            if (!itemSet.has(k)) {
              itemSet.add(k);
              itemOrder.push({ key: k, categorie_principale: b.categorie_principale, item_budget: b.item_budget });
            }
          }
        }

        // Map montant par (item, site) — somme les sous-lignes par bâtiment
        const siteBudgetMaps = {};
        for (const site of groupSites) {
          const map = {};
          for (const b of (allBudgets[site.id] || [])) {
            const k = `${b.categorie_principale}||${b.item_budget}`;
            map[k] = (map[k] || 0) + parseFloat(b.montant || 0);
          }
          siteBudgetMaps[site.id] = map;
        }

        const expenseItems = itemOrder.filter(i => (i.categorie_principale || '') !== 'REVENUS');
        const revenueItems = itemOrder.filter(i => (i.categorie_principale || '') === 'REVENUS');

        // En-tête du groupe
        rows.push([`${provinceLabel} - ${currencyLabel}`, '', ...groupSites.map(() => ''), '']);
        rows.push([
          t('category'), t('budgetItem'),
          ...groupSites.map(s => s.nom_site || ''),
          'Total'
        ]);

        // Lignes dépenses
        for (const item of expenseItems) {
          const vals = groupSites.map(s => siteBudgetMaps[s.id][item.key] || 0);
          rows.push([
            getCategoryLabel(item.categorie_principale),
            getItemLabel(item.item_budget),
            ...vals,
            vals.reduce((a, b) => a + b, 0)
          ]);
        }

        // Sous-total dépenses
        const expTotals = groupSites.map(s => expenseItems.reduce((sum, item) => sum + (siteBudgetMaps[s.id][item.key] || 0), 0));
        rows.push(['', t('subtotal') + ' ' + t('totalExpenses'), ...expTotals, expTotals.reduce((a, b) => a + b, 0)]);
        rows.push(['', '', ...groupSites.map(() => ''), '']);

        // Lignes revenus
        for (const item of revenueItems) {
          const vals = groupSites.map(s => siteBudgetMaps[s.id][item.key] || 0);
          rows.push([
            getCategoryLabel(item.categorie_principale),
            getItemLabel(item.item_budget),
            ...vals,
            vals.reduce((a, b) => a + b, 0)
          ]);
        }

        // Sous-total revenus
        if (revenueItems.length > 0) {
          const revTotals = groupSites.map(s => revenueItems.reduce((sum, item) => sum + (siteBudgetMaps[s.id][item.key] || 0), 0));
          rows.push(['', t('subtotal') + ' ' + t('totalRevenues'), ...revTotals, revTotals.reduce((a, b) => a + b, 0)]);
        }

        // Total net (dépenses - revenus) par site
        const netTotals = groupSites.map(s => {
          const exp = expenseItems.reduce((sum, item) => sum + (siteBudgetMaps[s.id][item.key] || 0), 0);
          const rev = revenueItems.reduce((sum, item) => sum + (siteBudgetMaps[s.id][item.key] || 0), 0);
          return exp - rev;
        });
        rows.push(['', t('operatingBudgetTotal'), ...netTotals, netTotals.reduce((a, b) => a + b, 0)]);
        rows.push([]);
        rows.push([]);
      }

      // Uniformiser la largeur des lignes
      const maxCols = Math.max(...rows.map(r => r.length), 3);
      rows.forEach(r => { while (r.length < maxCols) r.push(''); });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      const cols = [{ wch: 30 }, { wch: 40 }];
      for (let i = 2; i < maxCols; i++) cols.push({ wch: 20 });
      ws['!cols'] = cols;
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('operatingBudget'));
      XLSX.writeFile(wb, `budget-exploitation-detaille-${dateStr}.xlsx`);
    } catch (error) {
      console.error('Error exporting detailed operating budget:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ py: 1, flexWrap: 'wrap', gap: 1 }}>
          <BusinessIcon sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {t('sitesList')}
          </Typography>
          <FormControl sx={{ m: 1, minWidth: 140 }} size="small">
            <InputLabel sx={{ color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LanguageIcon fontSize="small" />
                Langue
              </Box>
            </InputLabel>
            <Select
              value={language}
              onChange={handleLanguageChange}
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              }}
            >
              <MenuItem value="fr">🇫🇷 Français</MenuItem>
              <MenuItem value="en">🇬🇧 English</MenuItem>
              <MenuItem value="pt">🇵🇹 Português</MenuItem>
              <MenuItem value="mg">🇲🇬 Malagasy</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={t('userGuide')}>
            <IconButton color="inherit" onClick={handleDownloadUserGuide} sx={{ ml: 1 }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('myProfile')}>
            <IconButton color="inherit" onClick={() => navigate('/profile')} sx={{ ml: 1 }}>
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('logout')}>
            <IconButton color="inherit" onClick={onLogout} sx={{ ml: 1 }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        {/* En-tête de la liste */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          mb: 3,
          p: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderRadius: 3,
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {t('sitesList')}
              </Typography>
              <Chip
                label={isFiltered ? `${sortedSites.length} / ${sitesList.length}` : sortedSites.length}
                color="primary"
                sx={{ fontWeight: 700, fontSize: '1rem', height: 32 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isFiltered ? t('filteredSites') : t('totalSites')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<AdminIcon />}
                onClick={() => navigate('/admin')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                {t('administration')}
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              startIcon={<DownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setExcelMenuAnchor(e.currentTarget)}
              sx={{
                px: 3,
                py: 1.5,
                borderWidth: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              {t('exportData')}
            </Button>
            <Menu
              anchorEl={excelMenuAnchor}
              open={Boolean(excelMenuAnchor)}
              onClose={() => setExcelMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setExcelMenuAnchor(null); exportToPdf(); }}>
                <ListItemIcon><PdfIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
                <ListItemText>{t('exportSiteListPdf')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportAssessmentsPdf} disabled={exportingAssessments || sortedSites.length === 0}>
                <ListItemIcon>
                  {exportingAssessments
                    ? <CircularProgress size={18} />
                    : <AssessmentIcon fontSize="small" sx={{ color: '#d32f2f' }} />}
                </ListItemIcon>
                <ListItemText>{t('exportAssessmentsPdf')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setExcelMenuAnchor(null); exportToExcel(); }}>
                <ListItemIcon><ExcelIcon fontSize="small" sx={{ color: '#2e7d32' }} /></ListItemIcon>
                <ListItemText>{t('exportSiteList')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportConsolidatedOperatingBudget}>
                <ListItemIcon><AccountBalanceIcon fontSize="small" sx={{ color: '#1565c0' }} /></ListItemIcon>
                <ListItemText>{t('exportConsolidatedOperating')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportConsolidatedInvestmentBudget}>
                <ListItemIcon><TrendingUpIcon fontSize="small" sx={{ color: '#e65100' }} /></ListItemIcon>
                <ListItemText>{t('exportConsolidatedInvestment')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={exportDetailedOperatingBudget}>
                <ListItemIcon><ViewColumnIcon fontSize="small" sx={{ color: '#00838f' }} /></ListItemIcon>
                <ListItemText>{t('exportDetailedOperating')}</ListItemText>
              </MenuItem>
            </Menu>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/site/new')}
              sx={{
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                },
              }}
            >
              {t('addSite')}
            </Button>
          </Box>
        </Box>

        {/* Carte des sites (réductible) */}
        <Paper sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setMapOpen(!mapOpen)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Carte des sites
              </Typography>
            </Box>
            <IconButton size="small">
              {mapOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={mapOpen}>
            <Box sx={{ px: 2, pb: 2 }}>
              <SiteMap sites={sortedSites} onSiteClick={handleMapSiteClick} />
            </Box>
          </Collapse>
        </Paper>

        {/* Barre de recherche et tri */}
        <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder={t('searchSites')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'background.paper',
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              select
              label={t('sortBy')}
              value={sortColumn || ''}
              onChange={handleSortColumnChange}
              sx={{
                minWidth: 220,
                flex: { xs: 1, sm: 'none' },
                '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'background.paper' }
              }}
            >
              <MenuItem value="">{t('sortDefault')}</MenuItem>
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
              ))}
            </TextField>
            <Tooltip title={t('toggleSortDirection')}>
              <span>
                <IconButton
                  disabled={!sortColumn}
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                >
                  {sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Liste des sites en tuiles */}
        {sitesList.length === 0 ? (
          <Paper sx={{ borderRadius: 3, py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Aucun site enregistré
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              Commencez par ajouter votre premier site
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/site/new')}
            >
              {t('addSite')}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {sortedSites.map((site) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={site.id}>
                <Card sx={{
                  borderRadius: 3,
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 6 }
                }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      gutterBottom
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
                      onClick={() => navigate(`/site/${site.id}?tab=0`)}
                    >
                      {site.nom_site}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {site.ville && <Chip label={site.ville} size="small" variant="outlined" />}
                      {site.pays && <Chip label={site.pays} size="small" variant="outlined" />}
                      {site.province && (
                        <Chip label={t(`provinces.${site.province}`)} size="small" variant="outlined" color="secondary" />
                      )}
                      {site.devise_pays && (
                        <Chip label={t(`currencies.${site.devise_pays}`)} size="small" color="primary" variant="outlined" />
                      )}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: alpha('#f093fb', 0.05) }, borderRadius: 1, px: 0.5, mx: -0.5 }}
                        onClick={() => navigate(`/site/${site.id}?tab=2`)}
                      >
                        <Typography variant="body2" color="text.secondary">{t('expensesAmount')}</Typography>
                        <Chip
                          label={formatNumber(site.montant_depenses)}
                          size="small"
                          sx={{ bgcolor: alpha('#f093fb', 0.1), color: '#f5576c', fontWeight: 600, cursor: 'pointer' }}
                        />
                      </Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: alpha('#43a047', 0.05) }, borderRadius: 1, px: 0.5, mx: -0.5 }}
                        onClick={() => navigate(`/site/${site.id}?tab=2`)}
                      >
                        <Typography variant="body2" color="text.secondary">{t('revenuesAmount')}</Typography>
                        <Chip
                          label={formatNumber(site.montant_revenus)}
                          size="small"
                          sx={{ bgcolor: alpha('#43a047', 0.1), color: '#2e7d32', fontWeight: 600, cursor: 'pointer' }}
                        />
                      </Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: alpha('#4facfe', 0.05) }, borderRadius: 1, px: 0.5, mx: -0.5 }}
                        onClick={() => navigate(`/site/${site.id}?tab=3`)}
                      >
                        <Typography variant="body2" color="text.secondary">{t('investmentBudget')}</Typography>
                        <Chip
                          label={formatNumber(site.budget_investissement)}
                          size="small"
                          sx={{ bgcolor: alpha('#4facfe', 0.1), color: '#00a0e3', fontWeight: 600, cursor: 'pointer' }}
                        />
                      </Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: alpha('#667eea', 0.05) }, borderRadius: 1, px: 0.5, mx: -0.5 }}
                        onClick={() => navigate(`/site/${site.id}?tab=1`)}
                      >
                        <Typography variant="body2" color="text.secondary">{t('riskScores')}</Typography>
                        {site.minScore !== null && site.maxScore !== null ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={site.minScore.toFixed(2)}
                              size="small"
                              sx={{ bgcolor: getScoreColor(site.minScore), color: 'white', fontWeight: 600, minWidth: 50, cursor: 'pointer' }}
                            />
                            <Typography variant="body2">-</Typography>
                            <Chip
                              label={site.maxScore.toFixed(2)}
                              size="small"
                              sx={{ bgcolor: getScoreColor(site.maxScore), color: 'white', fontWeight: 600, minWidth: 50, cursor: 'pointer' }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('riskCount')}</Typography>
                        <Chip
                          label={site.riskCount || 0}
                          size="small"
                          sx={{
                            bgcolor: site.riskCount > 0 ? alpha('#667eea', 0.1) : alpha('#999', 0.1),
                            color: site.riskCount > 0 ? '#667eea' : '#999',
                            fontWeight: 600, minWidth: 40
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'center', px: 2, py: 1 }}>
                    <Tooltip title={t('exportOperatingBudget')}>
                      <IconButton color="success" size="small" onClick={() => exportSiteOperatingBudget(site)}>
                        <ExcelIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('exportInvestmentBudget')}>
                      <IconButton size="small" onClick={() => exportSiteInvestmentBudget(site)} sx={{ color: '#e65100' }}>
                        <TrendingUpIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Graphique radar des risques">
                      <IconButton color="secondary" size="small" onClick={() => handleOpenRadarChart(site)}>
                        <BarChartIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('generatePdf')}>
                      <IconButton color="error" size="small" onClick={() => handleGeneratePdf(site.id, site.nom_site)}>
                        <PdfIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('edit')}>
                      <IconButton color="primary" size="small" onClick={() => handleEdit(site.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('delete')}>
                      <IconButton color="error" size="small" onClick={() => handleDelete(site)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le site <strong>"{siteToDelete?.nom_site}"</strong> ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            sx={{ ml: 1 }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog du graphique radar */}
      <Dialog
        open={radarDialogOpen}
        onClose={() => setRadarDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Analyse des risques - {selectedSiteForRadar?.nom_site}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedSiteForRadar?.ville && `${selectedSiteForRadar.ville}, `}
              {selectedSiteForRadar?.pays}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', height: 500, mt: 2 }} id="radar-chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#ccc" />
                <PolarAngleAxis
                  dataKey="thematique"
                  tick={{ fill: '#666', fontSize: 11 }}
                  tickSize={10}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 4]}
                  tick={{ fill: '#666' }}
                />
                <Radar
                  name="Score moyen"
                  dataKey="score"
                  stroke="#667eea"
                  fill="#667eea"
                  fillOpacity={0.5}
                  strokeWidth={2}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </Box>

          {/* Légende des couleurs */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Échelle de criticité :
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#2e7d32' }} />
                <Typography variant="body2">Score ≤ 1 (Faible)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#fbc02d' }} />
                <Typography variant="body2">1 {'<'} Score ≤ 2 (Modéré)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#ed6c02' }} />
                <Typography variant="body2">2 {'<'} Score ≤ 3 (Élevé)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: '#d32f2f' }} />
                <Typography variant="body2">Score {'>'} 3 (Critique)</Typography>
              </Box>
            </Box>
          </Box>

          {/* Tableau récapitulatif */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Scores par thématique :
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Thématique</strong></TableCell>
                    <TableCell align="center"><strong>Score moyen</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {radarData
                    .filter(item => item.score > 0)
                    .sort((a, b) => b.score - a.score)
                    .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.thematique}</TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: getScoreColor(item.score),
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.9rem'
                            }}
                          >
                            {item.score}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleDownloadRadarChart}
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Télécharger le graphique
          </Button>
          <Button onClick={() => setRadarDialogOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default Dashboard;
